import i18n from '@/utils/i18n';
import React, { useCallback, useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  isShiftPressed,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import { NOTES_MAX_LENGTH } from '@/components/order-guide/constants';
import { Popover } from 'react-native-popper';
import { Pressable, TextInput, View } from 'react-native';
import { shadow } from '@/styles';
import { Switch } from '@usepepper/sprout-kit-ui';
import { Typography } from '@usepepper/ui/atoms/typography';
import type { RequestNoteSavePayload } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';

const POPOVER_WIDTH = 280;
const POPOVER_TEXT_INPUT_MIN_HEIGHT = 80;
const INPUT_HEIGHT = 32;

interface OrderEntryTableNotesCellProps {
  collectionId: string;
  onSave?: (payload: RequestNoteSavePayload) => Promise<void>;
  persistRequest: boolean;
  request: string | null;
  variantPackUUID: string;
}

/**
 * Editable cell for request notes in the order entry table.
 *
 * Renders a bordered text input box showing a truncated preview of the note.
 * When pressed, opens a popover with a multiline text input and a
 * "Keep for future orders" toggle. Saves via `onSave` when the popover
 * closes (outside click, backdrop tap, or Enter key), only if the note
 * text or persist flag changed.
 *
 * @param props - {@link OrderEntryTableNotesCellProps}
 * @param props.collectionId - Order guide collection the variant pack belongs to
 * @param props.onSave - Callback to persist the note; receives a {@link RequestNoteSavePayload}
 * @param props.persistRequest - Whether "Keep for future orders" is currently enabled
 * @param props.request - Current note text, or null if empty
 * @param props.variantPackUUID - The variant pack this note applies to
 */
export const OrderEntryTableNotesCell =
  React.memo<OrderEntryTableNotesCellProps>(
    ({ collectionId, onSave, persistRequest, request, variantPackUUID }) => {
      const [styles, theme] = useStyles();
      const [isOpen, setIsOpen] = useState(false);
      const [editedRequest, setEditedRequest] = useState(request ?? '');
      const [editedPersist, setEditedPersist] = useState(persistRequest);
      const anchorRef = useRef<View>(null);

      // Track the last-saved values so the next popover open shows the
      // optimistic state even if the prop hasn't updated yet.
      const lastSavedRequestRef = useRef(request);
      const lastSavedPersistRef = useRef(persistRequest);

      const handleOpen = useCallback(() => {
        setEditedRequest(lastSavedRequestRef.current ?? '');
        setEditedPersist(lastSavedPersistRef.current);
        setIsOpen(true);
      }, []);

      const handleClose = useCallback(async () => {
        setIsOpen(false);

        if (!onSave || !collectionId) {
          return;
        }

        const requestChanged =
          (editedRequest || '') !== (lastSavedRequestRef.current ?? '');
        const persistChanged = editedPersist !== lastSavedPersistRef.current;

        if (requestChanged || persistChanged) {
          const previousRequest = lastSavedRequestRef.current;
          const previousPersist = lastSavedPersistRef.current;

          lastSavedRequestRef.current = editedRequest || null;
          lastSavedPersistRef.current = editedPersist;

          try {
            await onSave({
              collectionId,
              persistRequest: editedPersist,
              request: editedRequest,
              variantPackUUID,
            });
          } catch {
            // Mutation failed — revert refs so the next open shows the
            // pre-edit value (collection was already rolled back by the mutator).
            lastSavedRequestRef.current = previousRequest;
            lastSavedPersistRef.current = previousPersist;
          }
        }
      }, [collectionId, editedPersist, editedRequest, onSave, variantPackUUID]);

      return (
        <View style={styles.cellContainer}>
          <Pressable onPress={handleOpen} ref={anchorRef}>
            <View style={[styles.inputBox, isOpen && styles.inputBoxFocused]}>
              <Typography
                color={
                  request
                    ? theme.sk.color.text.primary
                    : theme.sk.color.text.tertiary
                }
                numberOfLines={1}
                variant="paragraph-md"
              >
                {request || ''}
              </Typography>
            </View>
          </Pressable>
          <Popover
            animated={false}
            isOpen={isOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleClose();
              }
            }}
            placement="right top"
            shouldCloseOnOutsideClick
            shouldFlip
            shouldOverlapWithTrigger
            trigger={anchorRef}
          >
            <Popover.Backdrop />
            <Popover.Content>
              <View style={styles.popover}>
                <TextInput
                  autoFocus
                  maxLength={NOTES_MAX_LENGTH}
                  multiline
                  onChangeText={setEditedRequest}
                  onKeyPress={(e) => {
                    if (e.nativeEvent.key === Key.Enter && !isShiftPressed(e)) {
                      e.preventDefault();
                      handleClose();
                    }
                  }}
                  placeholder={i18n.t(
                    'sales_portal.order_entry.table.notes_placeholder',
                  )}
                  placeholderTextColor={theme.sk.color.text.tertiary}
                  style={styles.popoverTextInput}
                  textAlignVertical="top"
                  value={editedRequest}
                />
                <View style={styles.persistRow}>
                  <Switch
                    checked={editedPersist}
                    label={i18n.t(
                      'sales_portal.order_entry.table.notes_keep_for_future',
                    )}
                    onCheckedChange={setEditedPersist}
                    size="$28"
                  />
                </View>
              </View>
            </Popover.Content>
          </Popover>
        </View>
      );
    },
  );

const useStyles = createStyles((theme) => ({
  cellContainer: {
    flex: 1,
  },
  inputBox: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    height: INPUT_HEIGHT,
    overflow: 'hidden',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  inputBoxFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
  persistRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  popover: {
    ...shadow({
      opacity: 0.1,
      radius: 64,
      x: 0,
      y: 8,
    }),
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    width: POPOVER_WIDTH,
  },
  popoverTextInput: {
    color: theme.sk.color.text.primary,
    fontSize: theme.sk.typography.input.md.fontSize,
    minHeight: POPOVER_TEXT_INPUT_MIN_HEIGHT,
    padding: theme.sk.spacing.spaceMedium,
  },
}));
